/** Members: readable by everyone in the group, editable by admins. */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { PageScroll } from "../../components/layout/PageScroll";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Modal, useConfirm } from "../../components/ui/modal";
import { Spinner } from "../../components/ui/spinner";
import { useBalances, useGroup, useGroupId } from "../../components/layout/AppShell";
import { ApiError, api, type Member } from "../../lib/api";
import { formatSigned, toneClass } from "../../lib/money";
import { cn } from "../../lib/utils";
import { useMembers } from "../ledger/useLedger";

export function MembersPage() {
  const groupId = useGroupId();
  const queryClient = useQueryClient();
  const group = useGroup(groupId);
  const members = useMembers(groupId);
  const balances = useBalances(groupId);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Member | null>(null);
  const [askRemove, confirmRemove] = useConfirm();

  const isAdmin = group.data?.my_role === "admin";
  const currency = group.data?.base_currency ?? "USD";
  const balanceFor = new Map(
    (balances.data?.balances ?? []).map((balance) => [
      balance.member_id,
      balance.balance_base_cents,
    ]),
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["members", groupId] });
    void queryClient.invalidateQueries({ queryKey: ["group", groupId] });
    void balances.refetch();
  };

  const fail = (cause: unknown) =>
    setError(cause instanceof ApiError ? cause.message : "Something went wrong.");

  const add = useMutation({
    mutationFn: () =>
      api.post<Member>(`/groups/${groupId}/members`, {
        display_name: name.trim(),
        email: email.trim() || null,
      }),
    onSuccess: () => {
      setName("");
      setEmail("");
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const update = useMutation({
    mutationFn: (input: { id: number; body: Record<string, unknown> }) =>
      api.patch<Member>(`/groups/${groupId}/members/${input.id}`, input.body),
    onSuccess: () => {
      setError(null);
      setEditing(null);
      refresh();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/groups/${groupId}/members/${id}`),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  if (members.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <PageScroll width="medium">
      <div>
        {confirmRemove}
        <h1 className="display py-3 text-sm text-muted">Members</h1>

        {/*
          On a phone this screen is the "More" tab, so the things that leave it belong at
          the top — under fifteen member cards is nowhere anyone would look for Settings.
        */}
        <nav className="mb-4 flex flex-wrap gap-2 md:hidden">
          <Link to={`/g/${groupId}/stats`} className="chip">
            Stats
          </Link>
          {isAdmin ? (
            <Link to={`/g/${groupId}/admin`} className="chip">
              Admin
            </Link>
          ) : null}
          <Link to="/settings" className="chip">
            Settings
          </Link>
          <Link to="/" className="chip">
            All groups
          </Link>
        </nav>

        {error ? (
          <p
            role="alert"
            className="mb-3 animate-fade-in rounded-lg border border-owing/35 bg-owing/[0.08] px-3 py-2 text-sm"
          >
            {error}
          </p>
        ) : null}

        {isAdmin ? (
          <form
            className="mb-5 flex flex-col gap-2.5 rounded-xl border border-rule bg-sunken p-4 sm:flex-row sm:items-center sm:p-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim()) add.mutate();
            }}
          >
            {/*
              `w-full sm:flex-1`, never a bare `flex-1`. The form is a *column* on a
              phone, so `flex-1` sets `flex-basis: 0` on the vertical axis and overrides
              the height — these boxes were rendering 20px tall, which is why they kept
              looking cramped no matter how much padding went on.
            */}
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Name"
              aria-label="New member name"
              className="h-12 w-full min-w-0 px-4 sm:h-11 sm:flex-1 sm:px-3"
            />
            <Input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Email (optional)"
              aria-label="New member email"
              type="email"
              className="h-12 w-full min-w-0 px-4 sm:h-11 sm:flex-1 sm:px-3"
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={add.isPending || !name.trim()}
              className="shrink-0 sm:h-11 sm:rounded-lg sm:px-4"
            >
              Add member
            </Button>
          </form>
        ) : null}

        <ul className="flex flex-col gap-2">
          {(members.data ?? []).map((member) => {
            const balance = balanceFor.get(member.id) ?? 0;
            return (
              <li
                key={member.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-rule bg-raised px-4 py-3"
              >
                <span className="min-w-[8rem] flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-medium">{member.display_name}</span>
                    {member.role === "admin" ? (
                      <span className="shrink-0 rounded-full bg-accent/12 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent">
                        admin
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-muted">
                    {member.is_ghost
                      ? member.pending_email
                        ? `invited · ${member.pending_email}`
                        : "no account yet"
                      : "linked account"}
                  </span>
                </span>

                <span className={cn("num shrink-0 text-sm", toneClass(balance))}>
                  {formatSigned(balance, currency)}
                </span>

                {isAdmin ? (
                  <span className="flex shrink-0 gap-1.5">
                    <Button size="sm" onClick={() => setEditing(member)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() =>
                        askRemove({
                          title: `Remove ${member.display_name}?`,
                          body:
                            balance === 0
                              ? "They stay on every past entry; they just stop appearing in new ones."
                              : "They still have a balance, so this will be refused until it is settled.",
                          confirmLabel: "Remove",
                          destructive: true,
                          onConfirm: () => remove.mutate(member.id),
                        })
                      }
                    >
                      Remove
                    </Button>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>

        <p className="mt-4 text-xs leading-relaxed text-muted">
          Someone with no email is a ghost member: they count in splits, and they keep their
          balance when they later register with that address. You can add or correct that
          address at any point until they sign up.
        </p>

        <EditMemberDialog
          member={editing}
          onClose={() => setEditing(null)}
          busy={update.isPending}
          onSave={(body) => editing && update.mutate({ id: editing.id, body })}
        />
      </div>
    </PageScroll>
  );
}

/** Rename a member, change their role, and set the email a ghost will link with. */
function EditMemberDialog({
  member,
  onClose,
  busy,
  onSave,
}: {
  member: Member | null;
  onClose: () => void;
  busy: boolean;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [loadedFor, setLoadedFor] = useState<number | null>(null);

  // Reset the fields whenever a different member is opened.
  if (member && loadedFor !== member.id) {
    setLoadedFor(member.id);
    setDisplayName(member.display_name);
    setEmail(member.pending_email ?? "");
    setRole(member.role);
  }

  const linked = Boolean(member && !member.is_ghost);

  return (
    <Modal
      open={member !== null}
      onOpenChange={(open) => !open && onClose()}
      variant="sheet"
      title={`Edit ${member?.display_name ?? "member"}`}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={busy || !displayName.trim()}
            onClick={() =>
              onSave({
                display_name: displayName.trim(),
                role,
                ...(linked ? {} : { email: email.trim() }),
              })
            }
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="text-2xs uppercase tracking-widest text-muted">
          Display name
          <Input
            className="mt-1.5"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            aria-label="Display name"
          />
        </label>

        <label className="text-2xs uppercase tracking-widest text-muted">
          Email
          <Input
            className="mt-1.5"
            type="email"
            value={linked ? "" : email}
            disabled={linked}
            placeholder={linked ? "linked to an account" : "nobody@example.com"}
            aria-label="Member email"
            onChange={(event) => setEmail(event.target.value)}
          />
          <span className="mt-1.5 block text-xs normal-case tracking-normal text-muted">
            {linked
              ? "They have an account now, so their email is theirs to change from Settings."
              : "They are linked to this address the moment they register, balance and history intact. Leave it empty to cancel the invitation."}
          </span>
        </label>

        <fieldset className="text-2xs uppercase tracking-widest text-muted">
          <legend className="mb-1.5">Role</legend>
          <div className="flex gap-1.5">
            {(["member", "admin"] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setRole(value)}
                aria-pressed={role === value}
                className={cn("chip capitalize", role === value && "chip-on")}
              >
                {value}
              </button>
            ))}
          </div>
        </fieldset>
      </div>
    </Modal>
  );
}
